import {
    convertCssAniDurationToMS,
    removeLengthUnitFromCssVar
} from "../utilities/helper-function";

class ExpandableContent {
    constructor(element) {
        this.element = element;
        this.main = this.element.querySelector('.expandable-content__main');
        this.items = this.main.querySelectorAll('.btn, .checkbox-btn__input');
        this.btnContainer = this.element.querySelector('.expandable-content__btns');
        this.moreBtnContainer = this.element.querySelector('.expandable-content__btn-more');
        this.moreBtn = this.moreBtnContainer.querySelector('.btn');
        this.lessBtnContainer = this.element.querySelector('.expandable-content__btn-less');
        this.lessBtn = this.lessBtnContainer.querySelector('.btn');
        this.lineLimit = this.main.dataset.lineLimit;
        this.tabletLineLimit = this.main.dataset.tabletLineLimit;
        this.mobileLineLimit = this.main.dataset.mobileLineLimit;
        this.itemType = this.main.dataset.itemType;
        this.isFirstTabPress = false;
        this.isExpanded = false;
        this.hasHiddenContent = false;
        this.isResized = false;
        this.rootStyles = getComputedStyle(document.documentElement);
        this.aniFast= convertCssAniDurationToMS(this.rootStyles.getPropertyValue('--ani--fast').trim());
        if(this.itemType === "filterLink") {
            this.linkFilterHeight = removeLengthUnitFromCssVar(this.rootStyles.getPropertyValue('--btn-secondary-height').trim());
            this.linkFilterMobileHeight = removeLengthUnitFromCssVar(this.rootStyles.getPropertyValue('--btn-secondary-mobile-height').trim());
        }
        if(this.itemType === "checkboxFilter") {
            this.checkboxBtnHeight = removeLengthUnitFromCssVar(this.rootStyles.getPropertyValue('--checkbox-btn-height').trim());
        }

        this.mainGap = removeLengthUnitFromCssVar(this.rootStyles.getPropertyValue('--expandable-content-main-gap').trim());
        this.mobileBreakpoint = 768;
        this.tabletBreakpoint = 1200;

        this.lastWindowSize = 0;

        window.addEventListener('load', () => {
            this.lastWindowSize = window.innerWidth;
            this.maxHeight = this.main.scrollHeight;
            this.limitedHeight = this.getLimitedHeight();
            this.main.setAttribute('role', "group")

            if(!this.isAllContentsShown()) {
                this.hasHiddenContent = true;
                this.limitContent(true);
                this.btnContainer.classList.add('expandable-content__btns--show');
                this.changeItemsAccessibilitySettings();
            }
        })

        window.addEventListener('resize', () => {
            this.adjustHeightByResize();
        })

        this.moreBtn.addEventListener('click', () => {
            this.expandContent();
        });

        this.lessBtn.addEventListener('click', () => {
            this.limitContent();
        });

        this.lessBtn.addEventListener('keydown', (e) => {
            if (e.key === "Tab" && this.isFirstTabPress) {
                e.preventDefault();
                this.firstHiddenItem.focus({preventScroll: true});
                this.isFirstTabPress = false;
            }
        });
    }

    getLimitedHeight() {
        let contentHeight = 0;
        let gapHeight = 0;
        let limitedHeight = 0;

        switch(this.itemType) {
            case "checkboxFilter":
                contentHeight = this.checkboxBtnHeight;
                gapHeight = this.mainGap;
                break;
            case "filterLink":
                if(this.isWindowMobileSize()) {
                    contentHeight = this.linkFilterMobileHeight;
                } else {
                    contentHeight = this.linkFilterHeight;
                }
                gapHeight = this.mainGap;
                break;
        }

        if(this.mobileLineLimit !== undefined && this.isWindowMobileSize()) {
            limitedHeight = contentHeight * this.mobileLineLimit + gapHeight * (this.mobileLineLimit - 1) + 1;
        } else if (this.tabletLineLimit !== undefined && this.isWindowTabletSize()) {
            limitedHeight = contentHeight * this.tabletLineLimit + gapHeight * (this.tabletLineLimit - 1) + 1;
        } else {
            limitedHeight = contentHeight * this.lineLimit + gapHeight * (this.lineLimit - 1) + 1;
        }
        return limitedHeight;
    }

    adjustHeightByResize() {
        this.isResized = true;
        this.maxHeight = this.main.scrollHeight;

        if(this.isExpanded) {
            this.main.style.height = "auto";
        }

        // Resize to small
        if(window.innerWidth < this.lastWindowSize) {
            if(!this.hasHiddenContent) {
                if(!this.isAllContentsShown()) {
                    this.turnOnExpandableFunction(this.isExpanded);
                }
            } else {
                if(this.lastWindowSize > this.mobileBreakpoint) {
                    if(this.isWindowMobileSize() || (this.tabletLineLimit !== undefined && this.isWindowTabletSize())) {
                        this.limitedHeight = this.getLimitedHeight();
                    }
                }

                if(this.isAllContentsShown()) {
                    this.turnOffExpandableFunction();
                } else {
                    if(!this.isExpanded) {
                        this.main.style.height = this.limitedHeight + "px";
                        this.changeItemsAccessibilitySettings();
                    }
                }
            }
        } else {
            // Resize over tablet break point
            if(this.mobileBreakpoint <= this.lastWindowSize && this.lastWindowSize < this.tabletBreakpoint) {
                if((this.tabletLineLimit !== undefined && !this.isWindowTabletSize())) {
                    this.adjustHeightByResizeLarger();
                }
            }

            // Resize over mobile break point
            if(this.lastWindowSize < this.mobileBreakpoint) {
                if(!this.isWindowMobileSize() ) {
                    this.adjustHeightByResizeLarger();
                }
            }

            if(this.hasHiddenContent && this.isAllContentsShown()) {
                this.turnOffExpandableFunction();
            }
        }
        this.lastWindowSize = window.innerWidth === 0 ? 0 : window.innerWidth;
    }

    getLimitedBottomPos() {
       return window.scrollY === 0 ? this.main.getBoundingClientRect().top + this.limitedHeight : window.scrollY + this.main.getBoundingClientRect().top + this.limitedHeight;
    }

    adjustHeightByResizeLarger() {
        this.limitedHeight = this.getLimitedHeight();
        if(!this.isExpanded) {
            this.main.style.height = this.limitedHeight + "px";
            this.changeItemsAccessibilitySettings();
        }

        if(!this.isAllContentsShown()) {
            this.turnOnExpandableFunction(this.isExpanded);
        }
    }

    limitContent(isInitial) {
        this.main.classList.add('expandable-content__main--limited');
        this.isFirstTabPress = false;

        if(isInitial || this.isResized) {
            this.isResized = false;
            this.hasHiddenContent = true;
            this.main.style.height = this.limitedHeight + "px";
        }

        if(this.limitedHeight === "auto") {
            this.main.style.height = this.limitedHeight;
        } else {
            this.main.style.height = this.limitedHeight + "px";
        }
        this.moreBtnContainer.classList.add('expandable-content__btn-more--show');
        this.lessBtnContainer.classList.remove('expandable-content__btn-less--show');
        this.changeItemsAccessibilitySettings();
        this.isExpanded = false;

        if(!isInitial) this.toggleFocusOnButton();
    }

    expandContent() {
        this.main.classList.remove('expandable-content__main--limited');

        if(this.isResized) {
            this.main.style.height = "auto";
            setTimeout(() => {
                this.maxHeight = this.main.scrollHeight;
                this.main.style.height = this.maxHeight + "px";
            }, this.aniFast);
            this.isResized = false;
        } else {
            this.main.style.height = this.maxHeight + "px";
        }

        this.moreBtnContainer.classList.remove('expandable-content__btn-more--show');
        this.lessBtnContainer.classList.add('expandable-content__btn-less--show');
        this.removeAllItemsAccessibilityAttribute();
        this.toggleFocusOnButton();
        this.isFirstTabPress = true;
        this.isExpanded = true;
    }

    toggleFocusOnButton() {
        setTimeout(() => {
            if(this.isExpanded) {
                this.lessBtn.focus();
            } else {
                this.moreBtn.focus();
            }
        }, 10)
    }

    turnOnExpandableFunction(isExpanded) {
        this.btnContainer.classList.add('expandable-content__btns--show');
        this.hasHiddenContent = true;
        if(isExpanded) {
            this.lessBtnContainer.classList.add('expandable-content__btn-less--show');
        } else {
            this.limitContent(true);
        }
    }
    
    turnOffExpandableFunction() {
        this.main.classList.remove('expandable-content__main--limited');
        this.main.removeAttribute("style");
        this.btnContainer.classList.remove('expandable-content__btns--show');
        this.moreBtnContainer.classList.remove('expandable-content__btn-more--show');
        this.lessBtnContainer.classList.remove('expandable-content__btn-less--show');
        this.isResized = false;
        this.hasHiddenContent = false;
        this.removeAllItemsAccessibilityAttribute();
    }

    isWindowMobileSize() {
        return window.innerWidth < this.mobileBreakpoint;
    }

    isWindowTabletSize() {
        return window.innerWidth < this.tabletBreakpoint;
    }

    isAllContentsShown() {
        return this.maxHeight <= this.limitedHeight;
    }

    changeItemsAccessibilitySettings() {
        let appearsFirstHiddenItem = false;
        this.limitedBottomPos = this.getLimitedBottomPos();
        
        this.items.forEach((item) => {
            const btnBottomPos = window.scrollY === 0 ? item.getBoundingClientRect().bottom : window.scrollY + item.getBoundingClientRect().bottom;

            if(btnBottomPos > this.limitedBottomPos) {
                item.setAttribute('tabindex', '-1');
                item.setAttribute('aria-hidden', true);
                if(appearsFirstHiddenItem === false) {
                    this.firstHiddenItem = item;
                    appearsFirstHiddenItem = true;
                }
            } else {
                item.removeAttribute('tabindex');
                item.removeAttribute('aria-hidden');
            }
        })
    }

    removeAllItemsAccessibilityAttribute() {
        this.items.forEach((item) => {
            item.removeAttribute('tabindex');
            item.removeAttribute('aria-hidden');
        })
    }

    toggleItemsAccessibilityAttributes() {
        if (!this.isExpanded) {
            this.changeItemsAccessibilitySettings();
        } else {
            this.removeAllItemsAccessibilityAttribute();
        }
    }
}

export default ExpandableContent;